import { useContext, useCallback } from 'react'
import { ModalContext } from 'providers/ModalProvider'
import { Spinner } from 'components/loaders'
import { useTranslation } from 'react-i18next'
import { ResourceNameForm } from 'components/form/ResourceNameForm'
import { toast } from '@lib/toasts'

export const useModal = () => useContext(ModalContext)

export const usePendingModal = () => {
  const modal = useModal()
  const { t } = useTranslation()

  const open = useCallback(
    (message?: string) =>
      modal.open({
        content: (
          <>
            <Spinner className="mr-2" />
            {message ?? t('common.pleaseWait')}
          </>
        ),
        dismissable: false,
      }),
    [modal, t],
  )

  return { open }
}

interface ResourceNameModalConfig {
  title: string
  inputLabel: string
  initialValue?: string
  onSave: (name: string) => Promise<void>
  isSaving: boolean
  toastMessages: {
    success: {
      title: string
      content: (name: string) => string
    }
    error: {
      title: string
      content: string
    }
  }
}

export const useResourceNameModal = () => {
  const modal = useModal()

  const openResourceNameModal = useCallback(
    (config: ResourceNameModalConfig) => {
      const {
        title,
        inputLabel,
        initialValue = '',
        onSave,
        isSaving,
        toastMessages,
      } = config

      const id = modal.form({
        title,
        content: (
          <ResourceNameForm
            initialValue={initialValue}
            inputLabel={inputLabel}
            onCancel={() => modal.close(id)}
            onSave={(name: string) => {
              void onSave(name).then(() => {
                modal.close(id)
                toast({
                  title: toastMessages.success.title,
                  variant: 'success',
                  content: toastMessages.success.content(name),
                })
              }).catch(() => {
                toast({
                  title: toastMessages.error.title,
                  variant: 'error',
                  content: toastMessages.error.content,
                })
              })
            }}
            isSaving={isSaving}
          />
        ),
      })
      
      return id
    },
    [modal],
  )

  return { openResourceNameModal }
}
